import { z } from "zod";
import { SchemaType, SchemaTypeSchema } from "./schema";

// Base schema types (read-only)
export const BaseFieldSchema = z.object({
  xmlTag: z.string(),
  type: z.string(),
  required: z.boolean(),
  multiple: z.boolean().optional(),
  description: z.string().optional(),
  quickImportColumn: z.string().nullable().optional(), // Can be null or string
  references: z.string().nullable().optional(),
  links: z.array(z.string()).optional().default([]),
});

export const BaseRecordSchema = z.object({
  record: z.string(),
  fields: z.array(BaseFieldSchema),
});

export const BaseSchemaSchema = z.object({
  sourceFile: z.string().optional(),
  generatedAt: z.string().optional(),
  stats: z.object({
    recordCount: z.number(),
    fieldCount: z.number(),
  }).optional(),
  records: z.array(BaseRecordSchema),
});

// Template override types
export const LookupConfigSchema = z.object({
  route: z.string(),
  key: z.string(),
  label: z.string(),
  dependsOn: z.string().optional(),
});

export const ComputedConfigSchema = z.object({
  expr: z.string(),
});

export const ValidationRuleSchema = z.object({
  expr: z.string(),
  message: z.string().optional(),
  level: z.enum(['error', 'warning']).optional(),
});

// Schema for default array items with computed expressions per field per item
export const DefaultArrayFieldSettingSchema = z.object({
  required: z.boolean().optional(),
  hidden: z.boolean().optional(),
  readOnly: z.boolean().optional(),
});

export const DefaultArrayItemSchema = z.object({
  id: z.string().optional(),
  values: z.record(z.string(), z.unknown()), // Field values (fieldName -> value)
  computedExpressions: z.record(z.string(), z.string()).optional(), // Field expressions (fieldName -> expression)
  label: z.string().optional(), // Optional label for the default item (e.g., "Commission", "Other Fees")
  fieldSettings: z.record(z.string(), DefaultArrayFieldSettingSchema).optional(),
});

export const FieldOverrideSchema = z.object({
  hidden: z.boolean().optional(),
  required: z.boolean().optional(),
  default: z.unknown().optional(),
  readOnly: z.boolean().optional(),
  help: z.string().optional(),
  friendlyName: z.string().optional(), // User-friendly display name for the field
  options: z.array(z.string()).optional(),
  lookup: LookupConfigSchema.optional(),
  computed: ComputedConfigSchema.optional(),
  // UI-only: mark field as editable per additional transaction
  // When enabled, this field will be shown in the "Additional Transactions" editor
  // for single-record requests that support multiple similar transactions.
  multiTxn: z.boolean().optional(),
  // Array group configuration (applies to first field in array group)
  defaultArrayItems: z.array(DefaultArrayItemSchema).optional(), // Pre-populated array items with expressions
  validate: z.union([ValidationRuleSchema, z.array(ValidationRuleSchema)]).optional(),
  requiredWhen: z.string().optional(),
});

// Multi-record configuration for combining records
export const MultiRecordConfigSchema = z.object({
  recordName: z.string(), // The base record name
  fieldOverrides: z.record(z.string(), FieldOverrideSchema).optional(), // Optional per-record field overrides
  order: z.number().optional(), // Display order in the form
});

// Virtual record configuration
export const VirtualRecordSchema = z.object({
  base: z.string().optional(), // DEPRECATED: Single base record (kept for backward compatibility)
  baseRecords: z.array(z.string()).optional(), // NEW: Multiple base records for multi-record forms
  multiRecordConfig: z.array(MultiRecordConfigSchema).optional(), // NEW: Detailed config for each record
  label: z.string(), // Display name (e.g., "Portfolio & Position Bundle")
  enabled: z.boolean(), // Whether this virtual record is enabled
  fieldOverrides: z.record(z.string(), FieldOverrideSchema).optional().default({}), // xmlTag -> override (for backward compat)
  createdAt: z.number(),
  createdBy: z.string(),
  isMultiRecord: z.boolean().optional(), // NEW: Flag to indicate multi-record virtual record
});

export const TemplateDocSchema = z.object({
  schemaVersion: z.string().default('v1'),
  schemaType: SchemaTypeSchema.default('geneva'),
  enabledRecords: z.record(z.string(), z.boolean()),
  fieldOverrides: z.record(z.string(), z.record(z.string(), FieldOverrideSchema)).optional().default({}),
  recordOrder: z.array(z.string()).optional(),
  virtualRecords: z.record(z.string(), VirtualRecordSchema).optional(), // recordId -> virtual record
  recordLookupFields: z.record(z.string(), z.array(z.object({
    name: z.string(), // Field name in the lookup table (e.g., "UDF.CCY")
    alias: z.string().optional(), // Optional alias for the field in the result
  }))).optional(), // recordName -> additional fields to fetch when this record is used as lookup
  recordQuantityChecks: z.record(z.string(), z.boolean()).optional(),
  updatedAt: z.number(),
  updatedBy: z.string(),
  name: z.string(),
  description: z.string().optional(),
  recordValidations: z.record(z.string(), z.array(ValidationRuleSchema)).optional(),
  validationTemplates: z.record(z.string(), z.object({
    expr: z.string(),
    async: z.boolean().optional(),
    params: z.array(z.string()).optional(),
  })).optional(),
});

// Inferred types
export type BaseField = z.infer<typeof BaseFieldSchema>;
export type BaseRecord = z.infer<typeof BaseRecordSchema>;
export type BaseSchema = z.infer<typeof BaseSchemaSchema>;
export type LookupConfig = z.infer<typeof LookupConfigSchema>;
export type ComputedConfig = z.infer<typeof ComputedConfigSchema>;
export type ValidationRule = z.infer<typeof ValidationRuleSchema>;
export type DefaultArrayItem = z.infer<typeof DefaultArrayItemSchema>;
export type FieldOverride = z.infer<typeof FieldOverrideSchema>;
export type MultiRecordConfig = z.infer<typeof MultiRecordConfigSchema>;
export type VirtualRecord = z.infer<typeof VirtualRecordSchema>;
export type TemplateDoc = z.infer<typeof TemplateDocSchema>;

// Array field grouping for fields that belong to an array structure
export interface ArrayFieldGroup {
  arrayName: string; // e.g., 'NonCapExpenses'
  fields: BaseField[];
}

// Extended types for UI
export interface FieldWithOverride extends BaseField {
  override?: FieldOverride;
  isOverridden?: boolean;
  arrayGroup?: string; // Name of the array this field belongs to
  arrayIndex?: number; // Index within the array (for rendering)
  originalXmlTag?: string; // Original xmlTag before unique ID transformation
}

export interface RecordWithMetadata extends BaseRecord {
  enabled: boolean;
  fieldCount: number;
  overriddenFieldCount: number;
  isVirtual?: boolean; // True for virtual records
  baseRecord?: string; // For single virtual records, references the base record
  baseRecords?: string[]; // For multi-record virtual records, references all base records
  isMultiRecord?: boolean; // True for multi-record virtual records
  label?: string; // Display label for virtual records
  arrayFieldGroups?: ArrayFieldGroup[]; // Grouped array fields
}

// Configuration types
export interface SchemaLoadConfig {
  url: string;
  fallbackUrls?: string[];
  cacheKey: string;
}

export interface TemplateConfig {
  orgId: string;
  templateId: string;
}

// UI State types
export interface SchemaConfiguratorState {
  schema: BaseSchema | null;
  template: TemplateDoc | null;
  selectedRecord: string | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  searchQuery: string;
  selectedField: string | null;
  drawerOpen: boolean;
  dirty: boolean;
}

// Export/Import types
export interface ExportData {
  template: TemplateDoc;
  metadata: {
    exportedAt: number;
    exportedBy: string;
    schemaVersion: string;
  };
}

// Validation result types
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

// Re-export validation options from shared functions
export interface ValidationOptions {
  asyncFunctions?: Record<string, (...args: any[]) => Promise<any>>;
  templateInstances?: Record<string, { templateName: string; params: Record<string, any> }>;
}

export interface GenevaSupportingItemSummary {
  code?: string | null;
  description?: string | null;
  name?: string | null;
  rawJson?: string;
}

export type GenevaSupportingFieldMap = Record<string, GenevaSupportingItemSummary>;

export interface GenevaSupportingMultiRecordPayload {
  _multiRecord: true;
  _virtualRecordId?: string;
  records: Record<string, GenevaSupportingFieldMap>;
}

export type GenevaSupportingData = Record<string, GenevaSupportingFieldMap> | GenevaSupportingMultiRecordPayload;

// Bulk action types
export type BulkAction =
  | 'hide-non-required'
  | 'clear-overrides'
  | 'set-all-optional'
  | 'import-options';

// Geneva request actions
export type GenevaRequestAction =
  | 'approve'
  | 'reject'
  | 'resubmit'
  | 'delete'
  | 'process'
  | 'retry';

// Save status
export type SaveStatus = 'saved' | 'saving' | 'error' | 'dirty';

// Geneva Request types
export type GenevaRequestStatus = 'draft' | 'pending' | 'approved' | 'processing' | 'completed' | 'error';

const GenevaSupportingItemSummarySchema = z.object({
  code: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  rawJson: z.string().optional(),
});

const GenevaSupportingFieldMapSchema = z.record(z.string(), GenevaSupportingItemSummarySchema);

const GenevaSupportingMultiRecordSchema = z.object({
  _multiRecord: z.literal(true),
  _virtualRecordId: z.string().optional(),
  records: z.record(z.string(), GenevaSupportingFieldMapSchema),
});

export const GenevaRequestSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  templateId: z.string(),
  schemaType: SchemaTypeSchema.default('geneva'),
  status: z.enum(["draft", "pending", "approved", "processing", "completed", "error"]),
  // status: z.string(),
  formData: z.record(z.string(), z.any()), // The actual form data
  metadata: z.object({
    recordType: z.string(), // Which record type(s) this request is for
    isMultiRecord: z.boolean().optional(),
    virtualRecordId: z.string().optional(),
    fieldCount: z.number(),
    arrayFieldCount: z.number().optional(),
    multiTransactionsCount: z.number().optional(),
  }),
  supportingData: z.union([GenevaSupportingFieldMapSchema, GenevaSupportingMultiRecordSchema]).optional(),
  audit: z.object({
    createdAt: z.number(),
    createdBy: z.string(),
    createdByEmail: z.string(),
    createdByName: z.string().optional(),
    updatedAt: z.number().optional(),
    updatedBy: z.string().optional(),
    updatedByEmail: z.string().optional(),
    updatedByName: z.string().optional(),
    approvedAt: z.number().optional(),
    approvedBy: z.string().optional(),
    approvedByEmail: z.string().optional(),
    approvedByName: z.string().optional(),
    // removed rejected fields
    rejectionReason: z.string().optional(),
  }),
  // set on requests created from LSEG corporate actions (functions-geneva/src/lseg_corporate_actions.ts)
  lsegCa: z.object({
    key: z.string(),
    type: z.enum(['Dividend', 'Split']),
    investment: z.string(),
    exDate: z.string(),
    source: z.string().optional(),
    triggeredByEmail: z.string().optional(),
  }).optional(),
  processing: z.object({
    xmlGenerated: z.boolean().optional(),
    xmlContent: z.string().optional(),
    genevaResponse: z.any().optional(),
    errorDetails: z.string().optional(),
  }).optional(),
});

export type GenevaRequest = z.infer<typeof GenevaRequestSchema>;

// Request creation payload
export interface CreateGenevaRequestData {
  orgId: string;
  templateId: string;
  schemaType?: SchemaType;
  formData: Record<string, any>;
  metadata: {
    recordType: string;
    isMultiRecord?: boolean;
    virtualRecordId?: string;
    fieldCount: number;
    arrayFieldCount?: number;
    multiTransactionsCount?: number;
  };
  supportingData?: GenevaSupportingData;
}

// Bulk request types
export interface FieldMapping {
  fieldName: string; // The Geneva field name (xmlTag)
  columnIndex: number; // Index of the column in the CSV/Excel file (-1 for default values)
  columnName: string; // Name/header of the column (empty for default values)
  isRequired: boolean; // Whether this field is required
  dataType: string; // Field type (text, number, date, etc.)
  defaultValue?: any; // Default value when columnIndex is -1
}

export interface ParsedFileData {
  headers: string[];
  rows: (string | number | null)[][];
  fileName: string;
  fileSize: number;
  rowCount: number;
}

// Firestore-compatible version with serialized arrays
export interface ParsedFileDataForStorage {
  headers: string[];
  rows: string; // JSON string
  fileName: string;
  fileSize: number;
  rowCount: number;
}

export const BulkGenevaRequestSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  templateId: z.string(),
  schemaType: SchemaTypeSchema.default('geneva'),
  status: z.enum(["draft", "pending", "approved", "processing", "completed", "error"]),
  isBulk: z.literal(true), // Distinguishes bulk requests from individual requests
  recordType: z.string(), // Which record type this bulk request is for
  fieldMappings: z.record(z.string(), z.object({
    columnIndex: z.number(),
    columnName: z.string(),
    isRequired: z.boolean(),
    dataType: z.string(),
  })), // fieldName -> mapping info
  parsedData: z.object({
    headers: z.array(z.string()),
    rows: z.string(), // Serialized JSON string to avoid nested arrays
    fileName: z.string(),
    fileSize: z.number(),
    rowCount: z.number(),
  }),
  bulkFormData: z.string(), // Serialized JSON string to avoid nested arrays
  metadata: z.object({
    recordType: z.string(),
    totalRecords: z.number(),
    validRecords: z.number(),
    errorRecords: z.number(),
    isMultiRecord: z.boolean().optional(),
    virtualRecordId: z.string().optional(),
  }),
  audit: z.object({
    createdAt: z.number(),
    createdBy: z.string(),
    createdByEmail: z.string(),
    createdByName: z.string().optional(),
    updatedAt: z.number().optional(),
    updatedBy: z.string().optional(),
    updatedByEmail: z.string().optional(),
    updatedByName: z.string().optional(),
    approvedAt: z.number().optional(),
    approvedBy: z.string().optional(),
    approvedByEmail: z.string().optional(),
    approvedByName: z.string().optional(),
    // removed rejected fields
    rejectionReason: z.string().optional(),
  }),
  processing: z.object({
    processedRecords: z.number().optional(),
    failedRecords: z.number().optional(),
    errorDetails: z.array(z.string()).optional(),
  }).optional(),
  geneva_responses: z.record(z.string(), z.object({
    code: z.string(),
    key_value: z.string(),
    status: z.string(),
    status_message: z.string(),
    timestamp: z.any(), // Firestore Timestamp
  })).optional(), // Record<string, GenevaResponse> where keys are "0", "1", "2", etc.
});

export type BulkGenevaRequest = z.infer<typeof BulkGenevaRequestSchema>;

// Bulk request creation payload
export interface CreateBulkGenevaRequestData {
  orgId: string;
  templateId: string;
  schemaType?: SchemaType;
  recordType: string;
  fieldMappings: Record<string, FieldMapping>;
  parsedData: ParsedFileData;
  bulkFormData: Record<string, any>[];
  metadata: {
    recordType: string;
    totalRecords: number;
    validRecords: number;
    errorRecords: number;
    isMultiRecord?: boolean;
    virtualRecordId?: string;
  };
} 
